/** Verdict reason line — real limiting factors only (never earnings). */

const TOP_SIG = /TOP_CONFIRMED|TOP_FORMING/i;
const BOTTOM_SIG = /BOTTOM_CONFIRMED|BOTTOM_FORMING/i;

function gateLines(result, max = 2) {
  const gate = result?.gate;
  if (!gate) return [];
  const out = [];
  for (const f of gate.failedCritical ?? []) {
    if (f.shortLabel && f.id !== "earnings_close") out.push(f.shortLabel);
  }
  for (const f of gate.failedAdvisory ?? []) {
    if (f.shortLabel && !/earnings/i.test(f.shortLabel)) out.push(f.shortLabel);
  }
  return out.slice(0, max);
}

function flowVolumeLines(result) {
  const lines = [];
  const cmf = result?.cmf;
  if (cmf != null && cmf <= -0.05) {
    lines.push(`Money leaving, CMF ${cmf >= 0 ? "+" : ""}${cmf.toFixed(2)}`);
  }
  const vd = result?.volDiv;
  if (vd?.bearish && vd.ratio != null) {
    lines.push(`Low-volume rally ${vd.ratio.toFixed(1)}×`);
  } else if (result?.rvol?.ratio != null && result.rvol.ratio < 0.85) {
    lines.push(`Low volume ${result.rvol.ratio.toFixed(1)}× avg`);
  }
  return lines;
}

function patternLines(result) {
  const sig = result?.signal ?? result?.module_signal ?? "";
  const lines = [];
  if (TOP_SIG.test(sig)) lines.push("Top / distribution pattern active");
  if (BOTTOM_SIG.test(sig)) lines.push("Bottom pattern — watch for confirmation");
  if (result?.rsiDiv?.bearishDivergence || result?.rsiBearishDivergence) {
    lines.push("RSI bearish divergence");
  }
  if (result?.rsiDiv?.bullishDivergence || result?.rsiBullishDivergence) {
    lines.push("RSI bullish divergence");
  }
  return lines;
}

function rrLine(result) {
  const rr = result?.rr1;
  const action = result?.scannerAction?.label ?? "";
  if (rr != null && rr < 1.5 && /CALLS|DIP/i.test(action)) {
    return `Reward:risk ${rr.toFixed(1)}:1 — below 1.5:1`;
  }
  return null;
}

function stripEarnings(text) {
  if (!text || typeof text !== "string") return "";
  if (/earnings/i.test(text)) return "";
  return text.trim();
}

function checklistReason(result, resolved) {
  const label = resolved?.label ?? "";
  if (/FORMING/i.test(label)) {
    const mom = result?.momentumChecklist;
    const puts = result?.putsChecklist;
    if (/CALLS/i.test(label) && mom?.reason) return stripEarnings(mom.reason);
    if (/PUTS/i.test(label) && puts?.reason) return stripEarnings(puts.reason);
  }
  if (/CALLS SETUP/i.test(label) && result?.momentumChecklist?.reason) {
    return stripEarnings(result.momentumChecklist.reason);
  }
  if (/PUTS SETUP/i.test(label) && result?.putsChecklist?.reason) {
    return stripEarnings(result.putsChecklist.reason);
  }
  const failed = [
    ...(result?.momentumChecklist?.failed ?? []),
    ...(result?.putsChecklist?.failed ?? []),
  ];
  const lines = failed.map((f) => f.shortLabel).filter(Boolean);
  return lines.slice(0, 2).join("; ");
}

/**
 * Top 2–3 limiting factors for badge subline + tooltip.
 * @returns {string} semicolon-separated; empty if entry-ready.
 */
export function buildLimitingReasonLine(result, scannerAction = null) {
  const a = scannerAction ?? result?.scannerAction;
  if (a?.tone === "go" && /ENTRY OK|TRIGGERED/i.test(a.label || "")) {
    return "";
  }

  const clReason = checklistReason(result, a);
  if (clReason) return clReason;

  const parts = [];
  parts.push(...gateLines(result, 2));
  parts.push(...flowVolumeLines(result));
  parts.push(...patternLines(result));

  const rr = rrLine(result);
  if (rr) parts.push(rr);

  const dipFailed = result?.dipChecklist?.failed ?? [];
  for (const f of dipFailed) {
    if (f?.shortLabel && !/earnings/i.test(f.shortLabel)) parts.push(f.shortLabel);
  }

  const unique = [...new Set(parts.filter(Boolean))];
  return unique.slice(0, 3).join("; ");
}
